import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import toast from "react-hot-toast";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  EventSubType,
  EventTaxonomyInUsePlace,
  EventType,
  EventTypeCatalogUsedEvent,
} from "../../models/events";
import { useGetBoMeQuery } from "../../store/services/api";
import {
  useDeleteEventSubTypeMutation,
  useDeleteEventTypeMutation,
  useGetEventSubTypeDetailQuery,
  useGetEventSubTypesPagedQuery,
  useGetEventTypeDetailQuery,
  useGetEventTypesPagedQuery,
  useGetEventTypesQuery,
  useResetEventTaxonomyMutation,
} from "../../store/services/events-types";
import { getApiErrorMessage } from "../../utils/functions/api-errors";
import { formatDateWithoutSeconds } from "../../utils/functions/date";
import { EventTypeForm } from "../Forms/EventTypeForm";
import { SubTypeForm } from "../Forms/SubTypeForm";
import { CATALOG_CONTENT_STYLE, CatalogCard, CatalogSearchField } from "../ui/CatalogCard";
import { emptyMessage } from "../ui/EmptyState";

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
const DEFAULT_ROWS = 10;

type SortOrder = 0 | 1 | -1 | null | undefined;

export type EventTaxonomyVariant = "types" | "subtypes";

interface EventTypesDVProps {
  variant: EventTaxonomyVariant;
}

const toSortOrderParam = (sortOrder: SortOrder): "asc" | "desc" =>
  sortOrder === -1 ? "desc" : "asc";

const toEventTypeSortBy = (sortField: string): "name" | "id" =>
  sortField === "eventTypeId" ? "id" : "name";

const toEventSubTypeSortBy = (sortField: string): "name" | "createDate" | "id" => {
  if (sortField === "createDate") return "createDate";
  if (sortField === "eventSubTypeId") return "id";
  return "name";
};

const GLOBAL_TYPE_IMPACT_MESSAGE =
  "Este tipo es global: lo comparten todos los tenants y el cambio impacta en todos.";
const GLOBAL_SUBTYPE_IMPACT_MESSAGE =
  "Este subtipo es global: lo comparten todos los tenants y el cambio impacta en todos.";

const taxonomyDeleteConfirmMessage = (entityLabel: "tipo" | "subtipo", inUse: boolean) =>
  inUse
    ? `Este ${entityLabel} está en uso. Se desactivará y no aparecerá en el catálogo, pero se mantendrá en los eventos y reglas que ya lo usan.`
    : `¿Eliminar este ${entityLabel}? Si ya está en uso se desactiva en lugar de borrarse, para no romper eventos y reglas.`;

const taxonomyDeletedToast = (entityLabel: "Tipo" | "Subtipo", inUse: boolean) =>
  inUse
    ? `${entityLabel} desactivado; se mantiene en los eventos y reglas`
    : `${entityLabel} eliminado`;

/**
 * GTK-701 — Un tipo con subtipos activos no se puede eliminar: el backend lo
 * rechaza con 409. Se detecta por el lugar de uso que ya devuelve el detalle,
 * para avisar antes de ofrecer el botón en vez de dejar que falle al confirmar.
 */
const hasActiveSubTypes = (places: EventTaxonomyInUsePlace[]): boolean =>
  places.some((place) => place.field === "subtypes");

const TaxonomyDeleteDialogBody = ({
  entityLabel,
  inUse,
  places,
}: {
  entityLabel: "tipo" | "subtipo";
  inUse: boolean;
  places: EventTaxonomyInUsePlace[];
}) => {
  if (entityLabel === "tipo" && hasActiveSubTypes(places)) {
    return (
      <>
        <p className="m-0 mb-3">
          Este tipo tiene subtipos activos y no se puede eliminar. Eliminá primero
          sus subtipos desde la sección Sub-Tipos de Eventos.
        </p>
        <ul className="m-0 pl-4">
          {places
            .filter((place) => place.field === "subtypes")
            .map((place) => (
              <li key={place.field}>{place.message}</li>
            ))}
        </ul>
      </>
    );
  }

  if (inUse && places.length > 0) {
    return (
      <>
        <p className="m-0 mb-3">
          Este {entityLabel} se desactivará y no va a aparecer para eventos, reglas o
          textos nuevos. Va a seguir activo en:
        </p>
        <ul className="m-0 pl-4">
          {places.map((place) => (
            <li key={place.field} className="mb-2">
              {place.message}
            </li>
          ))}
        </ul>
      </>
    );
  }

  return <p className="m-0">{taxonomyDeleteConfirmMessage(entityLabel, inUse)}</p>;
};

const GlobalTaxonomyImpactMessage = ({ variant }: { variant: EventTaxonomyVariant }) => (
  <Message
    className="w-full justify-content-start mb-3"
    severity="warn"
    text={variant === "types" ? GLOBAL_TYPE_IMPACT_MESSAGE : GLOBAL_SUBTYPE_IMPACT_MESSAGE}
  />
);

const FILTER_LABEL_GAP_PX = 6;
const FILTER_FIELD_GAP_PX = 18;

const CatalogDetailField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="col-12 md:col-6 p-1">
    <div className="surface-card border-1 surface-border border-round p-3 h-full">
      <small className="text-600 block mb-1">{label}</small>
      <span
        className="text-xl font-semibold text-900"
        style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
      >
        {children}
      </span>
    </div>
  </div>
);

const CatalogUsedEventsList = ({
  events,
  entityLabel,
  isLoading,
  isError,
  showTenant,
  tenantLabel,
}: {
  events: EventTypeCatalogUsedEvent[];
  entityLabel: "tipo" | "subtipo";
  isLoading: boolean;
  isError: boolean;
  showTenant: boolean;
  tenantLabel: (tenantId: string | null) => string;
}) => (
  <div>
    <small className="text-600 block mb-2">Eventos que lo usan</small>
    {isLoading ? (
      <div className="flex justify-content-center py-3">
        <ProgressSpinner style={{ width: "2rem", height: "2rem" }} />
      </div>
    ) : isError ? (
      <span className="text-500">No se pudieron cargar los eventos.</span>
    ) : events.length === 0 ? (
      <span className="text-500">
        No hay eventos que usen este {entityLabel} todavía.
      </span>
    ) : (
      <ul
        className="m-0 p-0 list-none flex flex-column gap-2"
        style={{ maxHeight: "22rem", overflow: "auto" }}
      >
        {events.map((event) => (
          <li
            key={event.id}
            className="p-3 border-1 surface-border border-round surface-card"
          >
            <Link
              to={`/events/${event.id}`}
              className="font-semibold text-900 no-underline hover:underline"
            >
              {event.name}
            </Link>
            <div className="text-600 text-sm mt-1">
              {formatDateWithoutSeconds(event.startDate)}
              {showTenant ? ` · ${tenantLabel(event.tenantId)}` : null}
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const EventTypesDV = ({ variant }: EventTypesDVProps) => {
  const isTypes = variant === "types";
  const scope = useTenantScope();
  const { data: me } = useGetBoMeQuery();
  const isSuperAdmin = scope.role === "SUPER_ADMIN";
  const tenantId = scope.tenantId;
  const isGlobalCatalog = !scope.loading && isSuperAdmin && tenantId === null;
  const canManage = isSuperAdmin || !!tenantId;
  const writeDisabled = scope.loading || (!isGlobalCatalog && !tenantId);

  const tenantLabels = useMemo(() => {
    const labels = new Map<string, string>();
    (me?.tenants ?? []).forEach((tenant) => labels.set(tenant.id, tenant.provider || tenant.id));
    return labels;
  }, [me?.tenants]);

  const scopeLabel = (tenantId: string | null | undefined) =>
    tenantId ? tenantLabels.get(tenantId) ?? tenantId : "Global";

  const [typeFirst, setTypeFirst] = useState(0);
  const [typeRows, setTypeRows] = useState(DEFAULT_ROWS);
  const [typeSortField, setTypeSortField] = useState("eventTypeName");
  const [typeSortOrder, setTypeSortOrder] = useState<SortOrder>(1);

  const [subTypeFirst, setSubTypeFirst] = useState(0);
  const [subTypeRows, setSubTypeRows] = useState(DEFAULT_ROWS);
  const [subTypeSortField, setSubTypeSortField] = useState("eventSubTypeName");
  const [subTypeSortOrder, setSubTypeSortOrder] = useState<SortOrder>(1);
  const [eventTypeIdFilter, setEventTypeIdFilter] = useState<number | null>(null);
  const [nameSearch, setNameSearch] = useState("");

  const [typeFormVisible, setTypeFormVisible] = useState(false);
  const [typeToEdit, setTypeToEdit] = useState<EventType>();
  const [typeToDelete, setTypeToDelete] = useState<EventType>();
  const [typeToView, setTypeToView] = useState<EventType>();

  const [subTypeFormVisible, setSubTypeFormVisible] = useState(false);
  const [subTypeToEdit, setSubTypeToEdit] = useState<EventSubType>();
  const [subTypeToDelete, setSubTypeToDelete] = useState<EventSubType>();
  const [subTypeToView, setSubTypeToView] = useState<EventSubType>();
  const [resetDialogVisible, setResetDialogVisible] = useState(false);

  const catalogSearch = nameSearch.trim();
  const { data: catalogTypes } = useGetEventTypesQuery(undefined, { skip: scope.loading });
  const { data: typesPage, isLoading: isLoadingTypes } = useGetEventTypesPagedQuery(
    {
      page: Math.floor(typeFirst / typeRows) + 1,
      limit: typeRows,
      sortBy: toEventTypeSortBy(typeSortField),
      sortOrder: toSortOrderParam(typeSortOrder),
      ...(tenantId ? { tenantId } : {}),
      ...(catalogSearch ? { search: catalogSearch } : {}),
    },
    { skip: scope.loading || !isTypes }
  );
  const { data: subTypesPage, isLoading: isLoadingSubTypes } = useGetEventSubTypesPagedQuery(
    {
      page: Math.floor(subTypeFirst / subTypeRows) + 1,
      limit: subTypeRows,
      sortBy: toEventSubTypeSortBy(subTypeSortField),
      sortOrder: toSortOrderParam(subTypeSortOrder),
      ...(eventTypeIdFilter !== null ? { eventTypeId: eventTypeIdFilter } : {}),
      ...(tenantId ? { tenantId } : {}),
      ...(catalogSearch ? { search: catalogSearch } : {}),
    },
    { skip: scope.loading || isTypes }
  );

  const typeFilterOptions = catalogTypes ?? [];

  const [deleteEventType, { isLoading: isDeletingType }] = useDeleteEventTypeMutation();
  const [deleteEventSubType, { isLoading: isDeletingSubType }] =
    useDeleteEventSubTypeMutation();
  const [resetEventTaxonomy, { isLoading: isResetting }] = useResetEventTaxonomyMutation();
  const {
    data: typeDetail,
    isFetching: isFetchingTypeDetail,
    isError: isTypeDetailError,
  } = useGetEventTypeDetailQuery(
    {
      id: typeToView?.eventTypeId ?? 0,
      ...(tenantId ? { tenantId } : {}),
    },
    { skip: !typeToView }
  );
  const {
    data: subTypeDetail,
    isFetching: isFetchingSubTypeDetail,
    isError: isSubTypeDetailError,
  } = useGetEventSubTypeDetailQuery(
    {
      id: subTypeToView?.eventSubTypeId ?? 0,
      ...(tenantId ? { tenantId } : {}),
    },
    { skip: !subTypeToView }
  );

  const hasOwnCatalog = isTypes
    ? typesPage?.hasOwnCatalog === true
    : subTypesPage?.hasOwnCatalog === true;

  useEffect(() => {
    if (!isLoadingTypes && typesPage && typesPage.total > 0 && typesPage.items.length === 0 && typeFirst > 0) {
      setTypeFirst(0);
    }
  }, [isLoadingTypes, typesPage, typeFirst]);

  useEffect(() => {
    if (
      !isLoadingSubTypes &&
      subTypesPage &&
      subTypesPage.total > 0 &&
      subTypesPage.items.length === 0 &&
      subTypeFirst > 0
    ) {
      setSubTypeFirst(0);
    }
  }, [isLoadingSubTypes, subTypesPage, subTypeFirst]);

  useEffect(() => {
    setEventTypeIdFilter(null);
    setSubTypeFirst(0);
  }, [tenantId]);

  useEffect(() => {
    if (eventTypeIdFilter === null || !catalogTypes) {
      return;
    }
    const filterTypeExists = catalogTypes.some((type) => type.eventTypeId === eventTypeIdFilter);
    if (!filterTypeExists) {
      setEventTypeIdFilter(null);
    }
  }, [eventTypeIdFilter, catalogTypes]);

  const getTypeName = (eventTypeId: number) =>
    typeFilterOptions.find((type) => type.eventTypeId === eventTypeId)?.eventTypeName || "";

  const closeTypeForm = () => {
    setTypeFormVisible(false);
    setTypeToEdit(undefined);
  };

  const closeSubTypeForm = () => {
    setSubTypeFormVisible(false);
    setSubTypeToEdit(undefined);
  };

  const handleTypePage = (event: DataTablePageEvent) => {
    setTypeFirst(event.first);
    setTypeRows(event.rows);
  };

  const handleTypeSort = (event: DataTableSortEvent) => {
    setTypeSortField(event.sortField);
    setTypeSortOrder(event.sortOrder);
    setTypeFirst(0);
  };

  const handleSubTypePage = (event: DataTablePageEvent) => {
    setSubTypeFirst(event.first);
    setSubTypeRows(event.rows);
  };

  const handleSubTypeSort = (event: DataTableSortEvent) => {
    setSubTypeSortField(event.sortField);
    setSubTypeSortOrder(event.sortOrder);
    setSubTypeFirst(0);
  };

  const handleDeleteType = () => {
    if (!typeToDelete || !canManage) return;

    const inUse = typeToDelete.inUse === true;

    deleteEventType(typeToDelete.eventTypeId)
      .unwrap()
      .then(() => {
        toast.success(taxonomyDeletedToast("Tipo", inUse));
        setTypeToDelete(undefined);
      })
      .catch((error) => {
        toast.error(getApiErrorMessage(error) || "No se pudo eliminar el tipo");
      });
  };

  const handleDeleteSubType = () => {
    if (!subTypeToDelete || !canManage) {
      return;
    }

    const inUse = subTypeToDelete.inUse === true;

    deleteEventSubType(subTypeToDelete.eventSubTypeId)
      .unwrap()
      .then(() => {
        toast.success(taxonomyDeletedToast("Subtipo", inUse));
        setSubTypeToDelete(undefined);
      })
      .catch((error) => {
        toast.error(getApiErrorMessage(error) || "No se pudo eliminar el subtipo");
      });
  };

  const handleReset = () => {
    resetEventTaxonomy()
      .unwrap()
      .then(() => {
        setEventTypeIdFilter(null);
        setSubTypeFirst(0);
        toast.success("Tipos reseteados");
        setResetDialogVisible(false);
      })
      .catch((error) => {
        toast.error(getApiErrorMessage(error) || "No se pudo resetear los tipos");
      });
  };

  const typeActionsTemplate = (eventType: EventType) => (
    <div className="flex gap-2">
      <Button
        type="button"
        icon="pi pi-eye"
        rounded
        outlined
        aria-label="Ver detalle del tipo"
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
        onClick={() => setTypeToView(eventType)}
      />
      {canManage ? (
        <>
          <Button
            type="button"
            icon="pi pi-pencil"
            rounded
            outlined
            severity="info"
            aria-label="Editar tipo"
            tooltip="Editar"
            tooltipOptions={{ position: "top" }}
            onClick={() => {
              setTypeToEdit(eventType);
              setTypeFormVisible(true);
            }}
          />
          <Button
            type="button"
            icon="pi pi-trash"
            rounded
            outlined
            severity="danger"
            aria-label="Eliminar tipo"
            tooltip="Eliminar"
            tooltipOptions={{ position: "top" }}
            onClick={() => setTypeToDelete(eventType)}
          />
        </>
      ) : null}
    </div>
  );

  const subTypeActionsTemplate = (subType: EventSubType) => (
    <div className="flex gap-2">
      <Button
        type="button"
        icon="pi pi-eye"
        rounded
        outlined
        aria-label="Ver detalle del subtipo"
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
        onClick={() => setSubTypeToView(subType)}
      />
      {canManage ? (
        <>
          <Button
            type="button"
            icon="pi pi-pencil"
            rounded
            outlined
            severity="info"
            aria-label="Editar subtipo"
            tooltip="Editar"
            tooltipOptions={{ position: "top" }}
            onClick={() => {
              setSubTypeToEdit(subType);
              setSubTypeFormVisible(true);
            }}
          />
          <Button
            type="button"
            icon="pi pi-trash"
            rounded
            outlined
            severity="danger"
            aria-label="Eliminar subtipo"
            tooltip="Eliminar"
            tooltipOptions={{ position: "top" }}
            onClick={() => setSubTypeToDelete(subType)}
          />
        </>
      ) : null}
    </div>
  );

  const resetDialog = (
    <Dialog
      contentStyle={CATALOG_CONTENT_STYLE}
      header="Restablecer catálogo"
      visible={resetDialogVisible}
      style={{ width: "32rem", maxWidth: "95vw" }}
      onHide={() => setResetDialogVisible(false)}
      footer={
        <div className="form-actions flex flex-wrap justify-content-end" style={{ gap: "var(--space-18)" }}>
          <Button
            type="button"
            label="Restablecer catálogo"
            severity="danger"
            loading={isResetting}
            onClick={handleReset}
          />
          <Button
            type="button"
            label="Cancelar"
            severity="secondary"
            outlined
            disabled={isResetting}
            onClick={() => setResetDialogVisible(false)}
          />
        </div>
      }
    >
      <p className="m-0">
        Las copias de este tenant se dan de baja y volvés a usar los tipos
        globales. Los eventos ya creados no se tocan.
      </p>
    </Dialog>
  );

  const catalogActions = (
    <>
      {canManage && (
        <Button
          type="button"
          icon="pi pi-plus"
          rounded
          size="small"
          aria-label={isTypes ? "Agregar tipo de eventos" : "Agregar subtipo de eventos"}
          tooltip={isTypes ? "Agregar tipo de eventos" : "Agregar subtipo de eventos"}
          disabled={writeDisabled}
          onClick={() => {
            if (isTypes) {
              setTypeToEdit(undefined);
              setTypeFormVisible(true);
            } else {
              setSubTypeToEdit(undefined);
              setSubTypeFormVisible(true);
            }
          }}
        />
      )}
      {!!tenantId && hasOwnCatalog && (
        <Button
          type="button"
          icon="pi pi-replay"
          rounded
          outlined
          size="small"
          severity="secondary"
          aria-label="Restablecer catálogo"
          tooltip="Restablecer catálogo"
          disabled={scope.loading || isResetting}
          onClick={() => setResetDialogVisible(true)}
        />
      )}
    </>
  );

  if (isTypes) {
    return (
      <CatalogCard heading="Tipos de eventos" actions={catalogActions}>
        <div
          className="flex flex-wrap align-items-end justify-content-between mb-4"
          style={{ gap: FILTER_FIELD_GAP_PX }}
        >
          <CatalogSearchField
            inputId="eventTypeSearch"
            value={nameSearch}
            onChange={(value) => {
              setNameSearch(value);
              setTypeFirst(0);
            }}
          />
        </div>
        {isGlobalCatalog ? <GlobalTaxonomyImpactMessage variant="types" /> : null}

        <DataTable
          value={typesPage?.items ?? []}
          lazy
          paginator
          dataKey="eventTypeId"
          first={typeFirst}
          rows={typeRows}
          totalRecords={typesPage?.total ?? 0}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onPage={handleTypePage}
          onSort={handleTypeSort}
          sortField={typeSortField}
          sortOrder={typeSortOrder}
          loading={scope.loading || isLoadingTypes}
          emptyMessage={emptyMessage("tipos", Boolean(nameSearch.trim()))}
        >
          <Column field="eventTypeName" header="Nombre" sortable />
          {isSuperAdmin && (
            <Column
              header="Alcance"
              body={(eventType: EventType) => scopeLabel(eventType.tenantId)}
              style={{ width: "12rem" }}
            />
          )}
          <Column header="Acciones" body={typeActionsTemplate} style={{ width: "12rem" }} />
        </DataTable>

        {typeFormVisible && canManage && (
          <Dialog
            contentStyle={CATALOG_CONTENT_STYLE}
            header={typeToEdit ? "Editar tipo" : "Nuevo tipo"}
            visible
            style={{ width: "32rem", maxWidth: "95vw" }}
            onHide={closeTypeForm}
          >
            {isGlobalCatalog ? <GlobalTaxonomyImpactMessage variant="types" /> : null}
            <EventTypeForm eventType={typeToEdit} handleClose={closeTypeForm} />
          </Dialog>
        )}

        {typeToDelete && canManage && (
          <Dialog
            contentStyle={CATALOG_CONTENT_STYLE}
            header="Eliminar tipo"
            visible
            style={{ width: "32rem", maxWidth: "95vw" }}
            onHide={() => setTypeToDelete(undefined)}
            footer={
              <div className="form-actions flex flex-wrap justify-content-end" style={{ gap: "var(--space-18)" }}>
                {!hasActiveSubTypes(typeToDelete.inUsePlaces ?? []) && (
                  <Button
                    type="button"
                    label="Eliminar tipo"
                    severity="danger"
                    loading={isDeletingType}
                    onClick={handleDeleteType}
                  />
                )}
                <Button
                  type="button"
                  label="Cancelar"
                  severity="secondary"
                  outlined
                  disabled={isDeletingType}
                  onClick={() => setTypeToDelete(undefined)}
                />
              </div>
            }
          >
            {!typeToDelete.tenantId ? <GlobalTaxonomyImpactMessage variant="types" /> : null}
            <TaxonomyDeleteDialogBody
              entityLabel="tipo"
              inUse={typeToDelete.inUse === true}
              places={typeToDelete.inUsePlaces ?? []}
            />
          </Dialog>
        )}

        <Dialog
          visible={!!typeToView}
          header="Detalle del tipo"
          modal
          style={{ width: "42rem", maxWidth: "95vw", maxHeight: "90vh" }}
          breakpoints={{ "960px": "95vw" }}
          contentStyle={{ ...CATALOG_CONTENT_STYLE, overflowY: "auto" }}
          onHide={() => setTypeToView(undefined)}
        >
          {typeToView ? (
            <div className="flex flex-column gap-4">
              <div className="flex flex-column gap-2">
                <h2
                  className="text-xl md:text-2xl font-semibold text-900 m-0"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {typeToView.eventTypeName}
                </h2>
                <div className="flex flex-wrap align-items-center gap-2">
                  <Tag severity="secondary" value={scopeLabel(typeToView.tenantId)} />
                </div>
              </div>
              <div className="grid m-0">
                <CatalogDetailField label="Alcance">
                  {scopeLabel(typeToView.tenantId)}
                </CatalogDetailField>
              </div>
              <CatalogUsedEventsList
                events={typeDetail?.usedInEvents ?? []}
                entityLabel="tipo"
                isLoading={isFetchingTypeDetail}
                isError={isTypeDetailError}
                showTenant={isSuperAdmin}
                tenantLabel={scopeLabel}
              />
            </div>
          ) : null}
        </Dialog>

        {resetDialog}
      </CatalogCard>
    );
  }

  return (
    <CatalogCard heading="Subtipos de eventos" actions={catalogActions}>
      <div
        className="flex flex-wrap align-items-end justify-content-between mb-4"
        style={{ gap: FILTER_FIELD_GAP_PX }}
      >
        <div className="flex flex-wrap align-items-end" style={{ gap: FILTER_FIELD_GAP_PX }}>
          <CatalogSearchField
            inputId="eventSubTypeSearch"
            value={nameSearch}
            onChange={(value) => {
              setNameSearch(value);
              setSubTypeFirst(0);
            }}
          />
          <div className="flex flex-column" style={{ maxWidth: "18rem", gap: FILTER_LABEL_GAP_PX }}>
            <label htmlFor="subtypeTypeFilter" className="text-600 text-sm">
              Tipo
            </label>
            <Dropdown
              inputId="subtypeTypeFilter"
              className="w-full"
              appendTo="self"
              value={eventTypeIdFilter}
              options={typeFilterOptions}
              optionLabel="eventTypeName"
              optionValue="eventTypeId"
              placeholder="Todos los tipos"
              showClear
              onChange={(event) => {
                setEventTypeIdFilter(event.value ?? null);
                setSubTypeFirst(0);
              }}
            />
          </div>
        </div>
      </div>
      {isGlobalCatalog ? <GlobalTaxonomyImpactMessage variant="subtypes" /> : null}

      <DataTable
        value={subTypesPage?.items ?? []}
        lazy
        paginator
        dataKey="eventSubTypeId"
        first={subTypeFirst}
        rows={subTypeRows}
        totalRecords={subTypesPage?.total ?? 0}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onPage={handleSubTypePage}
        onSort={handleSubTypeSort}
        sortField={subTypeSortField}
        sortOrder={subTypeSortOrder}
        loading={scope.loading || isLoadingSubTypes}
        emptyMessage={emptyMessage("subtipos", Boolean(nameSearch.trim()) || eventTypeIdFilter !== null)}
      >
        <Column
          field="eventTypeId"
          header="Tipo"
          body={(subType: EventSubType) => getTypeName(subType.eventTypeId)}
        />
        <Column field="eventSubTypeName" header="Subtipo" sortable />
        <Column
          field="createDate"
          header="Creado"
          sortable
          body={(subType: EventSubType) => formatDateWithoutSeconds(subType.createDate)}
        />
          {isSuperAdmin && (
            <Column
              header="Alcance"
              body={(subType: EventSubType) => scopeLabel(subType.tenantId)}
              style={{ width: "12rem" }}
            />
          )}
          <Column header="Acciones" body={subTypeActionsTemplate} style={{ width: "12rem" }} />
      </DataTable>

      {subTypeFormVisible && canManage && (
        <Dialog
          header={subTypeToEdit ? "Editar subtipo" : "Nuevo subtipo"}
          visible
          style={{ width: "32rem", maxWidth: "95vw" }}
          contentStyle={{ ...CATALOG_CONTENT_STYLE, overflow: "visible" }}
          onHide={closeSubTypeForm}
        >
          {isGlobalCatalog ? <GlobalTaxonomyImpactMessage variant="subtypes" /> : null}
          <SubTypeForm subTypeData={subTypeToEdit} handleClose={closeSubTypeForm} />
        </Dialog>
      )}

      {subTypeToDelete && canManage && (
        <Dialog
          contentStyle={CATALOG_CONTENT_STYLE}
          header="Eliminar subtipo"
          visible
          style={{ width: "32rem", maxWidth: "95vw" }}
          onHide={() => setSubTypeToDelete(undefined)}
          footer={
            <div className="form-actions flex flex-wrap justify-content-end" style={{ gap: "var(--space-18)" }}>
              <Button
                type="button"
                label="Eliminar subtipo"
                severity="danger"
                loading={isDeletingSubType}
                onClick={handleDeleteSubType}
              />
              <Button
                type="button"
                label="Cancelar"
                severity="secondary"
                outlined
                disabled={isDeletingSubType}
                onClick={() => setSubTypeToDelete(undefined)}
              />
            </div>
          }
        >
          {!subTypeToDelete.tenantId ? (
            <GlobalTaxonomyImpactMessage variant="subtypes" />
          ) : null}
            <TaxonomyDeleteDialogBody
              entityLabel="subtipo"
              inUse={subTypeToDelete.inUse === true}
              places={subTypeToDelete.inUsePlaces ?? []}
            />
          </Dialog>
        )}

        <Dialog
          visible={!!subTypeToView}
          header="Detalle del subtipo"
          modal
          style={{ width: "42rem", maxWidth: "95vw", maxHeight: "90vh" }}
          breakpoints={{ "960px": "95vw" }}
          contentStyle={{ ...CATALOG_CONTENT_STYLE, overflowY: "auto" }}
          onHide={() => setSubTypeToView(undefined)}
        >
          {subTypeToView ? (
            <div className="flex flex-column gap-4">
              <div className="flex flex-column gap-2">
                <h2
                  className="text-xl md:text-2xl font-semibold text-900 m-0"
                  style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                >
                  {subTypeToView.eventSubTypeName}
                </h2>
                <div className="flex flex-wrap align-items-center gap-2">
                  <Tag severity="secondary" value={scopeLabel(subTypeToView.tenantId)} />
                </div>
              </div>
              <div className="grid m-0">
                <CatalogDetailField label="Tipo">
                  {subTypeDetail?.eventTypeName ||
                    getTypeName(subTypeToView.eventTypeId) ||
                    "—"}
                </CatalogDetailField>
                <CatalogDetailField label="Alcance">
                  {scopeLabel(subTypeToView.tenantId)}
                </CatalogDetailField>
                <CatalogDetailField label="Creado">
                  {formatDateWithoutSeconds(subTypeToView.createDate) || "—"}
                </CatalogDetailField>
                {subTypeDetail?.updatedAt ? (
                  <CatalogDetailField label="Editado">
                    {formatDateWithoutSeconds(subTypeDetail.updatedAt)}
                  </CatalogDetailField>
                ) : null}
              </div>
              <CatalogUsedEventsList
                events={subTypeDetail?.usedInEvents ?? []}
                entityLabel="subtipo"
                isLoading={isFetchingSubTypeDetail}
                isError={isSubTypeDetailError}
                showTenant={isSuperAdmin}
                tenantLabel={scopeLabel}
              />
            </div>
          ) : null}
        </Dialog>

      {resetDialog}
    </CatalogCard>
  );
};
